import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';
import GuideCTA from '../components/GuideCTA';

const GuideRedigerCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>Rédiger un CV percutant: storytelling et formulations impactantes</title>
                <meta name="description" content="Apprenez à rédiger un CV qui marque les esprits: storytelling professionnel, formulations impactantes, adaptation par secteur et techniques de rédaction." />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/rediger-cv-percutant" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {"@type": "ListItem","position": 1,"name": "Accueil","item": "https://www.cv-analyse.fr/"},
                    {"@type": "ListItem","position": 2,"name": "Rédiger CV percutant","item": "https://www.cv-analyse.fr/guide/rediger-cv-percutant"}
                  ]
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": "Rédiger un CV percutant",
                  "description": "Apprenez à rédiger un CV qui marque les esprits",
                  "author": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "publisher": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "dateModified": "2026-08-15",
                  "mainEntityOfPage": {"@type": "WebPage", "@id": "https://www.cv-analyse.fr/guide/rediger-cv-percutant"}
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment rédiger un CV percutant",
                  "description": "Maîtrisez le storytelling professionnel et les techniques de rédaction pour créer un CV qui marque les recruteurs.",
                  "step": [
                    {"@type": "HowToStep", "name": "Construire son storytelling professionnel", "text": "Racontez votre parcours de façon cohérente et orientée vers le poste visé."},
                    {"@type": "HowToStep", "name": "Quantifier ses réalisations", "text": "Ajoutez des chiffres concrets à chaque expérience : +30% de CA, 10 personnes managées."},
                    {"@type": "HowToStep", "name": "Choisir des verbes d'action percutants", "text": "Commencez chaque bullet point par un verbe fort : piloté, développé, optimisé, conçu."},
                    {"@type": "HowToStep", "name": "Adapter le CV à chaque offre", "text": "Personnalisez l'accroche et les mots-clés selon le poste et l'entreprise visée."},
                    {"@type": "HowToStep", "name": "Tester son CV avec un outil IA", "text": "Utilisez CV Analyse pour valider que votre rédaction est optimisée pour les recruteurs et les ATS."}
                  ]
                }
                `}</script>
            </Helmet>

            {/* Fil d'Ariane */}
            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600" itemScope itemType="https://schema.org/BreadcrumbList">
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/" itemProp="item" className="hover:text-slate-900">
                                <span itemProp="name">Accueil</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </li>
                        <li className="text-slate-400">/</li>
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <span itemProp="name" className="text-slate-900">Rédiger CV percutant</span>
                            <meta itemProp="position" content="2" />
                        </li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">✍️</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            Rédiger un CV percutant
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Transformez votre CV en machine à décrocher des entretiens avec des formulations impactantes
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="rediger-cv-percutant" />

            {/* Table des matières */}
            <div className="card-surface mx-4 mt-4 max-w-4xl mx-auto">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Table des matières</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a href="#storytelling" className="flex items-center p-3 rounded-xl hover:bg-purple-50 transition-colors">
                            <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 text-purple-600 font-bold">1</span>
                            <span className="text-gray-700">Storytelling professionnel</span>
                        </a>
                        <a href="#formulations" className="flex items-center p-3 rounded-xl hover:bg-purple-50 transition-colors">
                            <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 text-purple-600 font-bold">2</span>
                            <span className="text-gray-700">Formulations impactantes</span>
                        </a>
                        <a href="#secteurs" className="flex items-center p-3 rounded-xl hover:bg-purple-50 transition-colors">
                            <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 text-purple-600 font-bold">3</span>
                            <span className="text-gray-700">Adaptation par secteur</span>
                        </a>
                        <a href="#erreurs" className="flex items-center p-3 rounded-xl hover:bg-purple-50 transition-colors">
                            <span className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3 text-purple-600 font-bold">4</span>
                            <span className="text-gray-700">Erreurs à éviter</span>
                        </a>
                    </div>
                </div>
            </div>

            <article className="px-6 py-16 mx-auto max-w-5xl">

                {/* Section 1: Storytelling professionnel */}
                <div id="storytelling" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">📖</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Storytelling professionnel</h2>
                        </div>
                        
                        <div className="bg-gradient-to-r from-purple-50 to-pink-100 rounded-2xl p-6 mb-6">
                            <h3 className="text-xl font-bold text-purple-900 mb-3">💡 Racontez votre parcours</h3>
                            <p className="text-gray-700">Un CV percutant raconte une histoire cohérente de votre évolution professionnelle, pas juste une liste d'expériences.</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-blue-900 mb-3">🎯 Fil conducteur</h3>
                                    <p className="text-gray-700 text-sm mb-4">Chaque expérience doit s'inscrire dans une progression logique :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Évolution des responsabilités</li>
                                        <li>• Développement des compétences</li>
                                        <li>• Croissance de l'impact</li>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-3">📈 Progression claire</h3>
                                <p className="text-gray-700 text-sm mb-4">Montrez votre évolution :</p>
                                <div className="space-y-2 text-sm">
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                                        <strong>2020-2021 :</strong> Développeur Junior
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-blue-500">
                                        <strong>2021-2022 :</strong> Développeur Senior
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-purple-500">
                                        <strong>2022-2023 :</strong> Lead Developer
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Formulations impactantes */}
                <div id="formulations" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-pink-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⚡</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Formulations impactantes</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-pink-50 to-pink-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-pink-900 mb-3">🎯 Verbes d'action puissants</h3>
                                    <p className="text-gray-700 text-sm mb-4">Utilisez des verbes qui montrent l'impact :</p>
                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                        <div className="bg-white p-2 rounded-lg text-center font-semibold text-green-600">Dirigé</div>
                                        <div className="bg-white p-2 rounded-lg text-center font-semibold text-green-600">Optimisé</div>
                                        <div className="bg-white p-2 rounded-lg text-center font-semibold text-green-600">Transformé</div>
                                        <div className="bg-white p-2 rounded-lg text-center font-semibold text-green-600">Innové</div>
                                        <div className="bg-white p-2 rounded-lg text-center font-semibold text-green-600">Créé</div>
                                        <div className="bg-white p-2 rounded-lg text-center font-semibold text-green-600">Accéléré</div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-orange-900 mb-3">📊 Chiffres et résultats</h3>
                                <p className="text-gray-700 text-sm mb-4">Quantifiez vos réalisations :</p>
                                <div className="space-y-3">
                                    <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                                        <p className="text-red-700 text-sm italic">❌ "Amélioré les performances"</p>
                                    </div>
                                    <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                                        <p className="text-green-700 text-sm">✅ "Amélioré les performances de 40%"</p>
                                    </div>
                                    <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                                        <p className="text-green-700 text-sm">✅ "Réduit les coûts de 25% (50k€ d'économies)"</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Adaptation par secteur */}
                <div id="secteurs" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🎯</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Adaptation par secteur</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-3">💻 Tech/IT</h3>
                                <p className="text-gray-700 text-sm mb-4">Mettez en avant :</p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Technologies maîtrisées</li>
                                    <li>• Projets innovants</li>
                                    <li>• Impact technique</li>
                                    <li>• Équipes managées</li>
                                </ul>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-3">💰 Finance</h3>
                                <p className="text-gray-700 text-sm mb-4">Privilégiez :</p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Résultats financiers</li>
                                    <li>• ROI et économies</li>
                                    <li>• Gestion de budgets</li>
                                    <li>• Conformité réglementaire</li>
                                </ul>
                            </div>
                            
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-3">📈 Marketing</h3>
                                <p className="text-gray-700 text-sm mb-4">Valorisez :</p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Croissance du CA</li>
                                    <li>• Campagnes réussies</li>
                                    <li>• Métriques de performance</li>
                                    <li>• Innovation créative</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 4: Erreurs à éviter */}
                <div id="erreurs" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⚠️</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Erreurs de rédaction à éviter</h2>
                        </div>
                        
                        <div className="space-y-4">
                            <div className="flex items-start p-4 bg-red-50 rounded-xl border-l-4 border-red-500">
                                <span className="text-red-500 text-xl mr-4">❌</span>
                                <div>
                                    <h3 className="font-bold text-red-900">Clichés génériques</h3>
                                    <p className="text-red-700 text-sm">Évitez "Dynamique, motivé, polyvalent" - ces mots n'apportent rien.</p>
                                </div>
                            </div>
                            <div className="flex items-start p-4 bg-red-50 rounded-xl border-l-4 border-red-500">
                                <span className="text-red-500 text-xl mr-4">❌</span>
                                <div>
                                    <h3 className="font-bold text-red-900">Formulations passives</h3>
                                    <p className="text-red-700 text-sm">Préférez "J'ai dirigé" plutôt que "J'étais responsable de".</p>
                                </div>
                            </div>
                            <div className="flex items-start p-4 bg-red-50 rounded-xl border-l-4 border-red-500">
                                <span className="text-red-500 text-xl mr-4">❌</span>
                                <div>
                                    <h3 className="font-bold text-red-900">Manque de spécificité</h3>
                                    <p className="text-red-700 text-sm">Soyez précis : "Géré une équipe de 8 développeurs" vs "Géré une équipe".</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <GuideCTA
                    title="La rédaction de votre CV est-elle percutante ?"
                    description="Notre IA évalue l'impact de chaque phrase et vous donne des suggestions concrètes pour convaincre les recruteurs — score et 3 conseils offerts."
                    ctaText="Analyser la rédaction de mon CV"
                    secondaryText="Télécharger mon CV"
                    secondaryLink="/generator"
                    theme="purple"
                />

                {/* Navigation entre guides */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/guide/analyse-cv-ia" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-blue-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-blue-700">Guide précédent</h3>
                            <p className="text-sm text-gray-600">Analyse de CV par IA</p>
                        </div>
                    </Link>
                    
                    <Link to="/guides" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-purple-700">Retour aux guides</h3>
                            <p className="text-sm text-gray-600">Voir tous nos guides CV</p>
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center ml-4 group-hover:bg-purple-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </div>
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideRedigerCV;
